import type { SoundCloudTrack } from '../types';

/**
 * Deduplicated, non-overlapping authentic joev noise SoundCloud catalog:
 * - Album / EP sets (individual sub-tracks accessed directly via the album/EP)
 * - Standalone singles & tribute releases with direct high-res artwork
 * - Unique IDs and zero key collisions
 */
const soundCloudData: SoundCloudTrack[] = [
  // --- Albums & EPs ---
  {
    id: 'sc-album-flash-of-energy-ep',
    title: 'Flash of Energy EP',
    description: 'Energetic minimal techno exploration across 4 dynamic tracks.',
    series: 'Album / EP',
    publishedAt: '06-2015',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/sets/flash-of-energy-ep',
    genre: 'Minimal Techno',
    duration: '18:44 min',
    accentColor: '#00ff00',
    image: 'https://i1.sndcdn.com/artworks-000022606124-99ymx3-t500x500.jpg',
  },
  {
    id: 'sc-album-purity-and-the-beast',
    title: 'Purity & The Beast',
    description: 'Sophisticated melodic journey exploring raw electronic rhythms.',
    series: 'Album / EP',
    publishedAt: '10-2015',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/sets/purity_and_the_beast',
    genre: 'IDM / Melodic Techno',
    duration: '31:24 min',
    accentColor: '#ff2d95',
    image: 'https://i1.sndcdn.com/artworks-000244848622-wi8yz8-t500x500.jpg',
  },
  {
    id: 'sc-album-clsr',
    title: 'CLSR EP',
    description: 'Hypnotic modular synthesis and deep ambient textures.',
    series: 'Album / EP',
    publishedAt: '04-2014',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/sets/clsr',
    genre: 'Ambient / Experimental',
    duration: '22:15 min',
    accentColor: '#00ff00',
    image: 'https://i1.sndcdn.com/artworks-000087586663-bs0l7i-t500x500.jpg',
  },
  {
    id: 'sc-album-arc',
    title: 'arc EP',
    description: 'Rich minimal electronic landscapes and soothing analog synthesis.',
    series: 'Album / EP',
    publishedAt: '02-2013',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/sets/arc-ep',
    genre: 'Minimal / Ambient',
    duration: '21:18 min',
    accentColor: '#ff2d95',
    image: 'https://i1.sndcdn.com/artworks-000042673511-fokgkk-t500x500.jpg',
  },
  {
    id: 'sc-album-out-of-order',
    title: 'Out of Order',
    description: 'Debut album showcasing signature dark-electronic soundscapes.',
    series: 'Album / EP',
    publishedAt: '12-2012',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/sets/out-of-order',
    genre: 'Electronic / IDM',
    duration: '36:42 min',
    accentColor: '#00ff00',
    image: 'https://i1.sndcdn.com/artworks-000158364335-6def72-t500x500.jpg',
  },
  {
    id: 'sc-album-kittys-journey',
    title: "Kitty's Journey",
    description: 'Deep narrative electronic progression exploring experimental sonic spaces.',
    series: 'Album / EP',
    publishedAt: '2016',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/sets/kittys-journey',
    genre: 'Experimental / Ambient',
    duration: '24:10 min',
    accentColor: '#ff2d95',
    image: 'https://i1.sndcdn.com/artworks-000556775721-7ntv0y-t500x500.jpg',
  },
  {
    id: 'sc-album-nmbd-mth',
    title: 'nmbd mth',
    description: 'Rhythmic analog modular arrangements and driving syncopated grooves.',
    series: 'Album / EP',
    publishedAt: '2017',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/sets/nmbd_mth',
    genre: 'Minimal / Techno',
    duration: '28:30 min',
    accentColor: '#00ff00',
    image: 'https://i1.sndcdn.com/artworks-000174843660-zlsl9a-t500x500.jpg',
  },

  // --- Standalone Singles & Tribute Releases ---
  {
    id: 'sc-track-tough-love',
    title: 'Tough Love (Sailor & I Tribute)',
    description: 'Single Tribute Release — deep atmospheric electronic piece.',
    series: 'Single / Track',
    publishedAt: '03-2020',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/tough-love-joevnoise-sailorandi-tribute',
    genre: 'Electronic / Melodic',
    duration: '04:12 min',
    accentColor: '#00ff00',
    image: 'https://i1.sndcdn.com/artworks-Tuzf2VgQa2EUiGT4-GpfTAg-t500x500.jpg',
  },
  {
    id: 'sc-track-k-bisasam',
    title: 'k (bisasam)',
    description: 'Glitchy IDM percussions with warm retro synth pads.',
    series: 'Single / Track',
    publishedAt: '2013',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/k-bisasam',
    genre: 'Glitch / IDM',
    duration: '03:58 min',
    accentColor: '#ff2d95',
    image: 'https://i1.sndcdn.com/artworks-u3cR53pz6GmaFZe5-6YCdHQ-t500x500.jpg',
  },
  {
    id: 'sc-track-unleash-joev-brake-deerside',
    title: 'Unleash (joev brake deerside)',
    description: 'High octane experimental rhythm cut from the Brake series.',
    series: 'Single / Track',
    publishedAt: '2019',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/unleash-joev-brake-deerside',
    genre: 'Electronic / Industrial',
    duration: '05:22 min',
    accentColor: '#00ff00',
    image: 'https://i1.sndcdn.com/artworks-000652393162-8gepm3-t500x500.jpg',
  },
  {
    id: 'sc-track-i-am-your-fader',
    title: 'I Am Your Fader',
    description: 'Hypnotic basslines with evolving modular filter sweeps.',
    series: 'Single / Track',
    publishedAt: '2018',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/i-am-your-fader',
    genre: 'Minimal Techno',
    duration: '06:14 min',
    accentColor: '#ff2d95',
    image: 'https://i1.sndcdn.com/artworks-000548417061-zgwcqm-t500x500.jpg',
  },
  {
    id: 'sc-track-piece-for-days-anet-reworked',
    title: 'Hi from Journey (aNET - reworked)',
    description: 'Ambient deconstruction and harmonic soundscape rework.',
    series: 'Single / Track',
    publishedAt: '2018',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/piece-for-days-anet-reworked',
    genre: 'Ambient / Drone',
    duration: '07:05 min',
    accentColor: '#00ff00',
    image: 'https://i1.sndcdn.com/artworks-000475730847-2m7dvz-t500x500.jpg',
  },
  {
    id: 'sc-track-superheld3in',
    title: 'Superheld<3in',
    description: 'Emotional synthesizer textures and intricate percussive sequencing.',
    series: 'Single / Track',
    publishedAt: '2017',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/superheld3in',
    genre: 'IDM / Downtempo',
    duration: '04:48 min',
    accentColor: '#ff2d95',
    image: 'https://i1.sndcdn.com/artworks-000483855213-213ofb-t500x500.jpg',
  },
  {
    id: 'sc-track-kittys-afterhour',
    title: "Kitty's Afterhour",
    description: 'Late night downtempo exploration with delicate reverb tails.',
    series: 'Single / Track',
    publishedAt: '2016',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/kittys-afterhour',
    genre: 'Downtempo / Chill',
    duration: '05:33 min',
    accentColor: '#00ff00',
    image: 'https://i1.sndcdn.com/artworks-000556123293-42e8y2-t500x500.jpg',
  },
  {
    id: 'sc-track-martin-kohlstedt-joev-deconstructs-exa',
    title: 'Martin Kohlstedt - joev deconstructs EXA (rework)',
    description: 'Neoclassical piano deconstruction transformed into modular electronics.',
    series: 'Single / Track',
    publishedAt: '2015',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/martin-kohlstedt-joev-deconstructs-exa',
    genre: 'Neoclassical / Electronic',
    duration: '05:51 min',
    accentColor: '#ff2d95',
    image: 'https://i1.sndcdn.com/artworks-000216386563-eva3z7-t500x500.jpg',
  },
  {
    id: 'sc-track-scene04',
    title: 'scene04',
    description: 'Cinematic electronic atmosphere and modular sound design.',
    series: 'Single / Track',
    publishedAt: '2015',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/scene04',
    genre: 'Cinematic / Ambient',
    duration: '04:15 min',
    accentColor: '#00ff00',
    image: 'https://i1.sndcdn.com/artworks-000187141799-gq90no-t500x500.jpg',
  },
  {
    id: 'sc-track-hrmny',
    title: 'hrmny',
    description: 'Polyphonic harmonic synthesis and minimal analog pulses.',
    series: 'Single / Track',
    publishedAt: '2014',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/hrmny',
    genre: 'Ambient / Melodic',
    duration: '05:02 min',
    accentColor: '#ff2d95',
    image: 'https://i1.sndcdn.com/artworks-000214718757-y9n6v6-t500x500.jpg',
  },
  {
    id: 'sc-track-outside-and-sun',
    title: 'Outside...and sun',
    description: 'Warm analog melodic leads and bright uplifting rhythm.',
    series: 'Single / Track',
    publishedAt: '2014',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/outside-and-sun',
    genre: 'Melodic Electronic',
    duration: '04:40 min',
    accentColor: '#00ff00',
    image: 'https://i1.sndcdn.com/artworks-000048422855-t66d3a-t500x500.jpg',
  },
  {
    id: 'sc-track-7th-april',
    title: '7th april',
    description: 'Reflective ambient composition with subtle tape flutter.',
    series: 'Single / Track',
    publishedAt: '2013',
    soundcloudUrl: 'https://soundcloud.com/joevnoise/7th-april',
    genre: 'Ambient / Tape Loop',
    duration: '04:20 min',
    accentColor: '#ff2d95',
    image: 'https://i1.sndcdn.com/artworks-000286974299-pbnis1-t500x500.jpg',
  },
];

export default soundCloudData;
